import React, { useEffect, useState } from "react";
import { api, API, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Pencil, Upload, Download, FileSpreadsheet, Globe2, Trash2 } from "lucide-react";

const EMPTY = { sku: "", name: "", category: "", unit: "CASE", distributor_price: 0, super_stockist_price: 0, case_weight_kg: 0, pieces_per_case: 0, scheme_text: "", scheme_struct: {}, status: "Active", region_id: "" };

function BulkUploadDialog({ open, onOpenChange, regions, defaultRegionId, onDone }) {
  const [regionId, setRegionId] = useState(defaultRegionId || "");
  const [file, setFile] = useState(null); const [busy, setBusy] = useState(false); const [preview, setPreview] = useState(null);
  useEffect(() => { if (open) { setRegionId(defaultRegionId || ""); setFile(null); setPreview(null); } }, [open, defaultRegionId]);
  async function downloadTemplate() {
    const token = localStorage.getItem("oms_token");
    const res = await fetch(`${API}/products/template.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
    const b = await res.blob(); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "products_template.xlsx"; a.click();
  }
  async function doPreview() {
    if (!file || !regionId) return toast.error("Region + file required");
    const fd = new FormData(); fd.append("file", file); fd.append("region_id", regionId);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/imports/price-list/preview`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed"); setPreview(d);
    } catch (e) { toast.error(e.message); }
  }
  async function doApply() {
    if (!file || !regionId) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("region_id", regionId);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/imports/price-list/apply`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed");
      toast.success(`Created ${d.created}, updated ${d.updated}`); onOpenChange(false); onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="heading">Bulk Upload Products</DialogTitle><p className="text-sm text-slate-500 mt-1">Products upload into the selected region's SKU master.</p></DialogHeader>
        <div className="grid grid-cols-3 gap-3 items-end">
          <div><Label>Region</Label>
            <Select value={regionId} onValueChange={setRegionId}><SelectTrigger data-testid="prod-bulk-region"><SelectValue placeholder="Select region first" /></SelectTrigger><SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="col-span-2"><Label>XLSX File</Label><Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0])} data-testid="prod-bulk-file" /></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="prod-template"><Download className="h-4 w-4 mr-1" /> Template</Button>
          <Button variant="outline" onClick={doPreview}><FileSpreadsheet className="h-4 w-4 mr-1" /> Preview</Button>
          <Button onClick={doApply} disabled={busy || !file || !regionId} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="prod-bulk-apply"><Upload className="h-4 w-4 mr-1" /> {busy ? "Uploading…" : "Apply"}</Button>
        </div>
        {preview && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-3 bg-slate-50 border-b text-sm">{preview.count} rows · <b>{preview.items.filter((i) => i.action === "create").length}</b> new · <b>{preview.items.filter((i) => i.action === "update").length}</b> updates</div>
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 sticky top-0"><tr><th className="p-2 text-left">Action</th><th className="p-2 text-left">SKU</th><th className="p-2 text-left">Name</th><th className="p-2 text-right">Dist</th><th className="p-2 text-right">SS</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.items.map((it, i) => (<tr key={i}><td className="p-2"><span className={`text-[10px] px-2 py-0.5 rounded border ${it.action === "create" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{it.action}</span></td><td className="p-2 mono">{it.sku}</td><td className="p-2">{it.name}</td><td className="p-2 text-right">{formatINR(it.distributor_price)}</td><td className="p-2 text-right">{formatINR(it.super_stockist_price)}</td></tr>))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RegionEditor({ open, onOpenChange, regions, onChanged }) {
  const [local, setLocal] = useState([]);
  const [newName, setNewName] = useState(""); const [newCode, setNewCode] = useState("");
  useEffect(() => { if (open) setLocal(regions.map((r) => ({ ...r }))); }, [open, regions]);
  async function save(r) {
    try { await api.patch(`/regions/${r.id}`, { name: r.name, code: r.code, gst_pct: r.gst_pct }); toast.success("Saved"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function create() {
    if (!newName.trim() || !newCode.trim()) return toast.error("Name & code required");
    try { await api.post("/regions", { name: newName, code: newCode, gst_pct: 18 }); setNewName(""); setNewCode(""); toast.success("Region added"); onChanged(); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function remove(r, { force = false } = {}) {
    try {
      await api.delete(`/regions/${r.id}${force ? "?force=true" : ""}`);
      toast.success(`Region '${r.name}' deleted`);
      onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed";
      if (e?.response?.status === 409 && window.confirm(`${detail}\n\nForce delete anyway? Dependent records will remain orphaned.`)) {
        return remove(r, { force: true });
      }
      toast.error(detail);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="heading">Region Master</DialogTitle><p className="text-sm text-slate-500 mt-1">Rename regions or add new ones. Names are used across distributors, products and reports.</p></DialogHeader>
        <div className="space-y-2">
          {local.map((r, idx) => (
            <div key={r.id} className="flex gap-2 items-end border border-slate-200 rounded-lg p-2.5">
              <div className="flex-1"><Label className="text-xs">Name</Label><Input value={r.name} onChange={(e) => { const c = [...local]; c[idx].name = e.target.value; setLocal(c); }} data-testid={`region-name-${r.id}`} /></div>
              <div className="w-20"><Label className="text-xs">Code</Label><Input value={r.code} onChange={(e) => { const c = [...local]; c[idx].code = e.target.value; setLocal(c); }} /></div>
              <Button size="sm" onClick={() => save(r)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`region-save-${r.id}`}>Save</Button>
              <Button size="sm" variant="outline" onClick={() => { if (window.confirm(`Delete region '${r.name}'? The server will block this if products/distributors are linked.`)) remove(r); }} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`region-delete-${r.id}`}>Delete</Button>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 pt-3 mt-2">
          <Label className="text-xs">Add new region</Label>
          <div className="flex gap-2 mt-1">
            <Input placeholder="Name (e.g. Karnataka)" value={newName} onChange={(e) => setNewName(e.target.value)} />
            <Input placeholder="Code*" value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} className="w-24" maxLength={8} />
            <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white">Add</Button>
          </div>
          <div className="text-[11px] text-amber-700 mt-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1"><b>Code is required</b> — 2–8 uppercase letters/digits (e.g. APTG, KA, HYD). This is the stable key that keeps products & distributors linked across restarts.</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const { user, can } = useAuth();
  const [list, setList] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionFilter, setRegionFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [regionsOpen, setRegionsOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [schemeJson, setSchemeJson] = useState("{}");
  const [selected, setSelected] = useState(new Set());
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  async function load() { const p = regionFilter ? { region_id: regionFilter } : {}; const { data } = await api.get("/products", { params: p }); setList(data); setSelected(new Set()); }
  async function loadRegions() { const { data } = await api.get("/regions"); setRegions(data); if (data[0] && !regionFilter) setRegionFilter(data[0].id); }
  useEffect(() => { loadRegions(); }, []);
  useEffect(() => { if (regionFilter) load(); /* eslint-disable-next-line */ }, [regionFilter]);

  function startEdit(d) { setForm({ ...EMPTY, ...d }); setEditId(d.id); setSchemeJson(JSON.stringify(d.scheme_struct || {}, null, 2)); setOpen(true); }
  function startNew() { setForm({ ...EMPTY, region_id: regionFilter }); setEditId(null); setSchemeJson("{}"); setOpen(true); }

  function toggle(id) { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} product(s)?`)) return;
    try { const { data } = await api.post("/products/bulk-delete", { ids: Array.from(selected) }); toast.success(`${data.deleted} deleted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function save() {
    try {
      let parsed = {}; try { parsed = schemeJson.trim() ? JSON.parse(schemeJson) : {}; } catch { return toast.error("Invalid scheme JSON"); }
      const payload = { ...form, distributor_price: Number(form.distributor_price), super_stockist_price: Number(form.super_stockist_price), case_weight_kg: Number(form.case_weight_kg), pieces_per_case: Number(form.pieces_per_case || 0), scheme_struct: parsed };
      if (editId) await api.patch(`/products/${editId}`, payload); else await api.post("/products", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  const filtered = list.filter((p) => !search || (p.sku + p.name + (p.category || "")).toLowerCase().includes(search.toLowerCase()));
  function toggleAll() { setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((p) => p.id))); }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Products</h1>
          <p className="text-sm text-slate-500 mt-1">Region-scoped SKU master. Distributor &amp; super-stockist pricing, case weights and schemes.</p>
        </div>
        {can([]) && (
          <div className="flex gap-2">
            {isAdmin && selected.size > 0 && (
              <Button onClick={bulkDelete} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="prod-bulk-delete">
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete {selected.size}
              </Button>
            )}
            <Button variant="outline" onClick={() => setRegionsOpen(true)} data-testid="regions-edit-btn"><Globe2 className="h-4 w-4 mr-1.5" /> Regions</Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="prod-bulk-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Bulk Upload</Button>
            <Button onClick={startNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-product-btn"><Plus className="h-4 w-4 mr-1.5" /> New Product</Button>
          </div>
        )}
      </div>
      <div className="flex gap-3 flex-wrap items-end">
        <div className="w-56"><Label>Region</Label>
          <Select value={regionFilter} onValueChange={setRegionFilter}><SelectTrigger data-testid="products-region-filter"><SelectValue /></SelectTrigger><SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <Input placeholder="Search SKU / name…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />
      </div>
      <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                {isAdmin && <th className="p-3.5 w-8"><Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleAll} data-testid="prod-select-all" /></th>}
                <th className="p-3.5 text-left">SKU</th>
                <th className="p-3.5 text-left">Name</th>
                <th className="p-3.5 text-left">Category</th>
                <th className="p-3.5 text-right">Dist Price</th>
                <th className="p-3.5 text-right">SS Price</th>
                <th className="p-3.5 text-right">Wt (kg)</th>
                <th className="p-3.5 text-left">Scheme</th>
                <th className="p-3.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => (
                <tr key={p.id} className="table-row-hover">
                  {isAdmin && <td className="p-3.5"><Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`prod-sel-${p.id}`} /></td>}
                  <td className="p-3.5 mono text-[12px] font-semibold">{p.sku}</td>
                  <td className="p-3.5">
                    <div className="text-slate-900 font-medium">{p.name && p.name !== p.sku ? p.name : <span className="text-amber-600 italic">— name missing —</span>}</div>
                  </td>
                  <td className="p-3.5">
                    {p.category && p.category !== "Uncategorised"
                      ? <span className="text-[11px] px-2 py-0.5 rounded border bg-slate-100 text-slate-700 border-slate-200 uppercase tracking-wide">{p.category}</span>
                      : <span className="text-[11px] px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">Uncategorised</span>}
                  </td>
                  <td className="p-3.5 text-right font-medium">{formatINR(p.distributor_price)}</td>
                  <td className="p-3.5 text-right text-slate-700">{formatINR(p.super_stockist_price)}</td>
                  <td className="p-3.5 text-right">{p.case_weight_kg}</td>
                  <td className="p-3.5 text-xs text-emerald-700">{p.scheme_text || "—"}</td>
                  <td className="p-3.5 text-right">{can([]) && <Button variant="ghost" size="icon" onClick={() => startEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="heading">{editId ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Region</Label>
              <Select value={form.region_id} onValueChange={(v) => setForm({ ...form, region_id: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            <div><Label>Distributor Price</Label><Input type="number" value={form.distributor_price} onChange={(e) => setForm({ ...form, distributor_price: e.target.value })} /></div>
            <div><Label>Super Stockist Price</Label><Input type="number" value={form.super_stockist_price} onChange={(e) => setForm({ ...form, super_stockist_price: e.target.value })} /></div>
            <div><Label>Case Weight (kg)</Label><Input type="number" value={form.case_weight_kg} onChange={(e) => setForm({ ...form, case_weight_kg: e.target.value })} /></div>
            <div><Label>Pieces per Case</Label><Input type="number" value={form.pieces_per_case} onChange={(e) => setForm({ ...form, pieces_per_case: e.target.value })} /></div>
            <div className="col-span-2"><Label>Scheme Text</Label><Input value={form.scheme_text} onChange={(e) => setForm({ ...form, scheme_text: e.target.value })} /></div>
            <div className="col-span-2"><Label>Scheme Struct (JSON)</Label><Textarea value={schemeJson} onChange={(e) => setSchemeJson(e.target.value)} rows={4} className="mono text-xs" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-product">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkUploadDialog open={bulkOpen} onOpenChange={setBulkOpen} regions={regions} defaultRegionId={regionFilter} onDone={load} />
      <RegionEditor open={regionsOpen} onOpenChange={setRegionsOpen} regions={regions} onChanged={loadRegions} />
    </div>
  );
}
